// ============================================================
// src/hooks/useAuth.ts (drop-in replacement)
// ============================================================
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction,
} from "react";
import api, { setToken } from "../services/api";
import { identifyUser, resetAnalytics, trackEvent, AnalyticsEvents } from "../services/analytics";

/* ---------- Types ---------- */
export interface Membership {
  // Freemium statuses (current): onboarding, free, paid, lapsed, cancelled, pending_migration.
  // Legacy (pre-freemium-launch, may appear on un-migrated rows): trialing, active, expired.
  status:
    | 'onboarding'
    | 'free'
    | 'paid'
    | 'lapsed'
    | 'cancelled'
    | 'pending_migration'
    | 'trialing'
    | 'active'
    | 'expired';
  trial_end_date: string | null;
  end_date: string | null;
  membership_id: number;
  auto_renew: boolean;
}

export interface ProgramInfo {
  name: string;
  status: string;
}

export interface User {
  id: number;
  email: string;
  role: string;
  profileComplete: boolean;
  membership: Membership | null;
  membershipActive: boolean; // convenience flag
  isAdmin: boolean; // convenience flag for admin access
  creatorProgram: ProgramInfo | null;
  chefProgram: ProgramInfo | null;
  hasPassword: boolean; // false for SSO-only users (Google/Apple) — UI uses this to hide password fields
  authProvider: 'email' | 'apple' | 'google' | string;
}

interface RegisterPayload {
  email: string;
  password: string;
  promoCode?: string;
  turnstileToken?: string;
  platform?: string;
}

interface SocialLoginPayload {
  provider: 'apple' | 'google';
  token: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  promoCode?: string;
  turnstileToken?: string;
  platform?: string;
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  setUser: Dispatch<SetStateAction<User | null>>;
  login: (email: string, password: string) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<void>;
  socialLogin: (payload: SocialLoginPayload) => Promise<{ isNewUser: boolean }>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

/* ---------- Helpers ---------- */
// Freemium model: our real statuses are onboarding, free, paid, lapsed, cancelled,
// pending_migration. (Legacy 'trialing'/'active'/'expired' may appear on un-migrated
// rows from before freemium launch — handled by the fallback below.)
const isMembershipActive = (membership: Membership | null): boolean => {
  if (!membership) return false;

  // Explicit blocks
  if (membership.status === 'cancelled') return false;
  // 'onboarding' = registered but hasn't picked a plan yet. App.tsx routes them
  // to /membership via this false return so they finish picking before using the app.
  if (membership.status === 'onboarding') return false;

  const now = new Date();

  // Subscription end_date takes precedence — applies to 'paid' (current Stripe sub),
  // 'lapsed' (formerly paid), and any other status with a defined end_date.
  if (membership.end_date) {
    return new Date(membership.end_date) > now;
  }

  // Legacy fallback for pre-freemium 'trialing' users (not yet migrated).
  if (membership.status === 'trialing' && membership.trial_end_date) {
    return new Date(membership.trial_end_date) > now;
  }

  // No end_date — 'free' and 'pending_migration' have ongoing app access.
  return true;
};

const toCamel = (u: any): User => {
  const membership = u.membership || null;
  const role = u.role || 'user';
  return {
    id: u.id ?? u.user_id,
    email: u.email,
    role,
    profileComplete: u.profile_complete ?? u.profileComplete ?? false,
    membership,
    membershipActive: isMembershipActive(membership),
    isAdmin: role === 'admin' || role === 'super-admin',
    creatorProgram: u.creator_program ?? null,
    chefProgram: u.chef_program ?? null,
    // Default to true for backward compat — older JWT-decoded users won't have
    // this field, but they're password-based accounts (SSO is newer).
    hasPassword: u.has_password ?? u.hasPassword ?? true,
    authProvider: u.auth_provider ?? u.authProvider ?? 'email',
  };
};

// minimal base64url decode (no extra deps)
function decodeJwtPayload(token: string): any | null {
  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    // pad to multiple of 4
    const padded = base64 + "===".slice((base64.length + 3) % 4);
    const json = decodeURIComponent(
      atob(padded)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function isExpired(token: string): boolean {
  const p = decodeJwtPayload(token);
  if (!p || typeof p.exp !== "number") return true;
  const now = Math.floor(Date.now() / 1000);
  return p.exp <= now;
}

const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);

/* ---------- Provider ---------- */
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // On mount: clear invalid/expired token; otherwise set it and load profile.
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || isExpired(token)) {
      setToken(null);       // clears axios header + localStorage (your helper)
      setUser(null);
      setLoading(false);
      return;
    }
    setToken(token);
    api
      .get("/users/profile")
      .then((res) => {
        const userData = toCamel(res.data.user);
        setUser(userData);
        // Analytics: identify returning user
        identifyUser(userData.id, { $email: userData.email });
      })
      .catch(() => {
        setToken(null);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  /* ----- Auth actions ----- */
  const login = async (email: string, password: string) => {
    // Ensure we do NOT send a stale Authorization header on the login call
    setToken(null);
    const res = await api.post("/users/login", { email, password });
    setToken(res.data.token); // persists + sets axios default
    // Login response has membership at same level as user, not nested inside
    // Merge it into the user object before calling toCamel
    const userWithMembership = { ...res.data.user, membership: res.data.membership };
    const userData = toCamel(userWithMembership);
    setUser(userData);

    // Analytics: identify user and track login
    identifyUser(userData.id, { $email: userData.email });
    trackEvent(AnalyticsEvents.LOGIN);
  };

  const register = async (payload: RegisterPayload) => {
    // Same idea: avoid sending stale Authorization on register
    setToken(null);
    const res = await api.post("/users/register", { platform: 'web', ...payload });
    setToken(res.data.token);
    // Register response has membership at same level as user, not nested inside
    const userWithMembership = { ...res.data.user, membership: res.data.membership };
    const userData = toCamel(userWithMembership);
    setUser(userData);

    // Analytics: identify user and track sign up
    identifyUser(userData.id, { $email: userData.email });
    trackEvent(AnalyticsEvents.SIGN_UP);
  };

  const socialLogin = async (payload: SocialLoginPayload) => {
    setToken(null);
    const res = await api.post("/auth/social", { platform: 'web', ...payload });
    setToken(res.data.token);
    // Social login response has membership at same level as user, not nested inside
    const userWithMembership = { ...res.data.user, membership: res.data.membership };
    const userData = toCamel(userWithMembership);
    setUser(userData);

    // Analytics: identify user and track login/signup
    identifyUser(userData.id, { $email: userData.email });
    trackEvent(res.data.isNewUser ? AnalyticsEvents.SIGN_UP : AnalyticsEvents.LOGIN, {
      auth_provider: payload.provider,
    });

    return { isNewUser: res.data.isNewUser };
  };

  const logout = () => {
    setToken(null);
    setUser(null);

    // Analytics: track logout and reset user
    trackEvent(AnalyticsEvents.LOGOUT);
    resetAnalytics();
  };

  const refreshUser = async () => {
    try {
      const res = await api.get("/users/profile");
      setUser(toCamel(res.data.user));
    } catch (err) {
      console.error("Failed to refresh user:", err);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, setUser, login, register, socialLogin, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

/* ---------- Hook ---------- */
export const useAuth = () => useContext(AuthContext);
